// File: `typescript/apps/front-end/src/main/react/component/SimpleSelect.tsx`
import React from "react";
import {
    Select as ShadSelect,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    SelectLabel,
    SelectGroup,
    SelectSeparator,
} from "@/components/ui/select";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type OptionItem = {
    key: string;
    value: string;
    label: React.ReactNode;
    icon?: React.ElementType;
    group?: string;
    disabledReason?: string;
    dataPlaywrightId?: string;
};

type SimpleSelectProps = {
    value?: string;
    onValueChange?: (val: string) => void;
    options: OptionItem[];
    placeholder?: string;
    size?: "sm" | "default";
    className?: string;
    triggerClassName?: string;
    ariaLabel?: string;
    dataPlaywrightId?: string;
    tooltip?: React.ReactNode;
    disabled?: boolean;
    fullWidth?: boolean;
};

export function SimpleSelect({
                                 value,
                                 onValueChange,
                                 options,
                                 placeholder,
                                 size = "default",
                                 className,
                                  triggerClassName,
                                 ariaLabel,
                                 dataPlaywrightId,
                                 tooltip,
                                 disabled = false,
                                  fullWidth = false,
                             }: SimpleSelectProps) {
    const normalizedValue = typeof value === "string" && value.trim().length > 0 ? value : undefined;

    const renderOptionContent = (it: OptionItem) => {
        const Icon = it.icon;

        if (!Icon) {
            return it.label;
        }

        return (
            <>
                <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
                <span>{it.label}</span>
            </>
        );
    };

    const renderItem = (it: OptionItem) => {
        const item = (
            <SelectItem
                key={it.key}
                value={it.value}
                disabled={it.disabledReason != undefined}
                data-playwright-id={it.dataPlaywrightId}
                className={cn(
                    // SV-71 (round 29 D5): shadcn semantic tokens only, never the raw --text-main /
                    // --line-color / --muted variables. The former 68% / 78% / 80% hairline dilutions
                    // resolve to the plain `border-border`: an alpha hairline is a ratchet in
                    // flatXRestingSurfaces.contract.test.tsx (FI-065), and a new file may not join it.
                    "rounded-xl border border-transparent px-3 py-2 text-sm font-medium text-foreground transition-[background-color,border-color,color,box-shadow]",
                    "focus:border-border focus:bg-muted focus:text-foreground",
                    "data-[state=checked]:border-border data-[state=checked]:bg-muted data-[state=checked]:text-foreground",
                    "data-[disabled]:border-border data-[disabled]:bg-muted data-[disabled]:text-muted-foreground data-[disabled]:opacity-100"
                )}
            >
                {renderOptionContent(it)}
            </SelectItem>
        );

        if (it.disabledReason == undefined) {
            return item;
        }

        return (
            <Tooltip key={it.key}>
                <TooltipTrigger asChild>
                    <div className="rounded-xl cursor-not-allowed">{item}</div>
                </TooltipTrigger>
                <TooltipContent sideOffset={6}>{it.disabledReason}</TooltipContent>
            </Tooltip>
        );
    };

    const trigger = (
        <SelectTrigger
            className={cn(
                // `standard-hover` is the one hover wash; the trigger must not also flip its border
                // colour or paint a competing hover background, and it rests on a flat `bg-card`.
                "standard-hover max-w-full border-border bg-card text-foreground shadow-none",
                "data-[placeholder]:text-muted-foreground",
                "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                "disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100",
                // THE HEIGHT MUST BE WRITTEN WITH THE SAME VARIANT PREFIX THE BASE USES, or it is dead.
                // `SelectTrigger`'s base className carries `data-[size=sm]:h-8`, and `size` is passed
                // down as `data-size`. A bare `h-9` here does NOT override it: twMerge treats
                // `h-9` and `data-[size=sm]:h-8` as different conflict keys (the variant modifier is
                // part of the key), so BOTH survive into the class list, and at runtime the attribute
                // selector wins on specificity. Measured 2026-08-02: the trigger rendered 32px while
                // this line asked for 36px, beside a 36px QuestionsFilterChip whose own docstring
                // asserts the two match exactly — a claim that was false at runtime for as long as
                // both existed. Prefixing makes it the same key, so twMerge drops the base rule.
                //
                // The `default` branch below carried the identical dead override — `h-11` against the
                // base's `data-[size=default]:h-9`, so it RENDERED 36px while asking for 44px. Resolved
                // 2026-08-02 by writing what it actually renders (`h-9`) rather than by making it obey
                // the 44px it asked for: that branch has ~24 call sites, nearly all forms, so growing
                // every default select by 8px is an app-wide control-height retune that needs its own
                // measurement pass. This edit is behaviour-preserving by construction — the rendered
                // height was already 36px — and it removes the dead class so the next reader is not
                // told 44px by a line that never produced it.
                size === "sm"
                    ? "data-[size=sm]:h-9 rounded-full px-3 text-xs [@media(pointer:coarse)]:min-h-11 sm:text-sm [&_[data-slot=select-value]]:font-semibold"
                    : "h-9 rounded-2xl px-3.5 text-sm [&_[data-slot=select-value]]:font-medium",
                fullWidth ? "w-full" : "w-auto",
                "min-w-0 [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate",
                triggerClassName,
                className
            )}
            size={size}
            aria-label={ariaLabel}
            data-playwright-id={dataPlaywrightId}
            disabled={disabled}
        >
            <SelectValue placeholder={placeholder} />
        </SelectTrigger>
    );

    const optionListNode = useOptionListNode(options, renderItem);

    return (
        <ShadSelect
            value={value}
            onValueChange={onValueChange}
            disabled={disabled}
        >
            {tooltip ? (
                <Tooltip>
                    <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                    <TooltipContent sideOffset={4}>{tooltip}</TooltipContent>
                </Tooltip>
            ) : (
                trigger
            )}

            {optionListNode}
        </ShadSelect>
    );
}

/**
 * A sentinel that no real signature can equal, so the first render always computes.
 * A real signature is either "" (no items) or contains the "~" field separator.
 */
const UNCOMPUTED_SIGNATURE = "<uncomputed>";

/**
 * The option list, rebuilt only when the CONTENT of `options` changes (AGENTS.md rule 31).
 *
 * <p>Every call site builds its `options` array literal inline, so the identity changes on every render
 * of the owner. Radix registers each item's native option through a layout effect on the Select ROOT,
 * so a rebuilt list is a chained write per item on a component above it. Measured on devo,
 * `SelectItemText` is the top update scheduler on `MyDashboardPage` and the second on
 * `CreateDiscussionPage`.
 *
 * <p>The bail-out is by CONTENT, not by identity, for the same reason rule 31 gives for a merge helper
 * feeding a store: a payload rebuilt by its owner preserves no element identity, so an identity check
 * can never be true and the guard would be structurally unreachable. `label` and `icon` are compared by
 * identity because they are `ReactNode`/`ElementType` and nothing here can prove two of them equal; a
 * caller passing a freshly built element as a label therefore keeps rebuilding, correctly.
 *
 * <p>Guard: `simpleSelectItemChurn.spec.tsx`, including the case that proves a real content change
 * still rebuilds. A guard that only asserted "never rebuild" would ship a stale option list.
 */
function useOptionListNode(options: OptionItem[], renderItem: (item: OptionItem) => React.ReactNode) {
    const renderItemRef = React.useRef(renderItem);
    renderItemRef.current = renderItem;

    const signature = options
        .map((option) => [
            option.key,
            option.value,
            option.group ?? "",
            option.disabledReason ?? "",
            option.dataPlaywrightId ?? "",
        ].join("~"))
        .join("|");
    const labels = options.map((option) => option.label);
    const icons = options.map((option) => option.icon);

    const cache = React.useRef<{
        signature: string;
        labels: readonly React.ReactNode[];
        icons: readonly (React.ElementType | undefined)[];
        node: React.ReactNode;
    }>({ signature: UNCOMPUTED_SIGNATURE, labels: [], icons: [], node: null });

    const sameByIdentity = (a: readonly unknown[], b: readonly unknown[]) =>
        a.length === b.length && a.every((entry, index) => entry === b[index]);

    if (
        cache.current.signature !== signature
        || !sameByIdentity(cache.current.labels, labels)
        || !sameByIdentity(cache.current.icons, icons)
    ) {
        const groups = new Map<string | null, OptionItem[]>();
        options.forEach((option) => {
            const group = option.group ?? null;
            if (!groups.has(group)) groups.set(group, []);
            groups.get(group)!.push(option);
        });

        cache.current = {
            signature,
            labels,
            icons,
            node: (
                <SelectContent
                    position="popper"
                    sideOffset={8}
                    className="z-layer-top max-h-80 min-w-[var(--radix-select-trigger-width)] rounded-2xl border-border bg-popover p-1.5 shadow-[var(--surface-shadow-md)] backdrop-blur-sm"
                >
                    {Array.from(groups.entries()).map(([group, items], index) => (
                        <React.Fragment key={group ?? `simple-select-group-${index}`}>
                            {index > 0 ? (
                                <SelectSeparator className="mx-2 my-1 bg-border" />
                            ) : null}

                            {group ? (
                                <SelectGroup>
                                    <SelectLabel className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                                        {group}
                                    </SelectLabel>
                                    {items.map((item) => renderItemRef.current(item))}
                                </SelectGroup>
                            ) : (
                                items.map((item) => renderItemRef.current(item))
                            )}
                        </React.Fragment>
                    ))}
                </SelectContent>
            ),
        };
    }

    return cache.current.node;
}

export default SimpleSelect;
